// Modules
import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import io, { Socket } from 'socket.io-client';
import { useAppSelector } from 'app/store';
import { selectAuthToken } from 'features/auth/authSlice';
import { API_URL } from '../config/api';

export interface SocketContextValue {
  socket: Socket | null;
  isConnected: boolean;
  addListener: <T>(event: string, callback: (data: T) => void) => void;
  removeListener: (event: string, callback: (...args: unknown[]) => void) => void;
  emit: <T>(event: string, data: T) => void;
}

export const SocketContext = createContext<SocketContextValue | null>(null);

interface SocketProviderProps {
  children: ReactNode;
}

export const SocketProvider: FC<SocketProviderProps> = ({ children }) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const socketRef = useRef<Socket | null>(null);
  const isConnectingRef = useRef(false);

  // Get auth token from Redux
  const token = useAppSelector(selectAuthToken);

  /**
   * Add an event listener to the socket
   */
  const addListener = useCallback(
    <T,>(event: string, callback: (data: T) => void) => {
      if (socketRef.current) {
        socketRef.current.on(event, callback as (...args: unknown[]) => void);
      }
    },
    [],
  );

  /**
   * Remove an event listener from the socket
   */
  const removeListener = useCallback(
    (event: string, callback: (...args: unknown[]) => void) => {
      if (socketRef.current) {
        socketRef.current.off(event, callback);
      }
    },
    [],
  );

  /**
   * Emit an event through the socket
   */
  const emit = useCallback(<T,>(event: string, data: T) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit(event, data);
    }
  }, []);

  // Socket connection management
  useEffect(() => {
    // Don't create socket if no token or already connecting
    if (!token) {
      // Disconnect existing socket if token was removed
      if (socketRef.current) {
        console.log('[Socket] Token removed, disconnecting');
        socketRef.current.disconnect();
        socketRef.current = null;
        setSocket(null);
        setIsConnected(false);
      }
      return;
    }

    // Prevent duplicate connections (React StrictMode)
    if (isConnectingRef.current || socketRef.current?.connected) {
      console.log('[Socket] Already connected or connecting, skipping');
      return;
    }

    // If there's an existing disconnected socket, clean it up
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    isConnectingRef.current = true;

    // Create new socket connection with autoConnect: false for more control
    const newSocket = io(API_URL || 'http://localhost:3000', {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      timeout: 20000,
      autoConnect: false, // We'll connect manually
    });

    newSocket.on('connect', () => {
      console.log('[Socket] Connected, id:', newSocket.id);
      setIsConnected(true);
      isConnectingRef.current = false;
    });

    newSocket.on('disconnect', (reason) => {
      console.log('[Socket] Disconnected:', reason);
      console.log('[Socket] Disconnect details - reason type:', typeof reason, 'value:', reason);
      // "io server disconnect" means the server actively disconnected us
      // This happens when the user is not found in the backend
      if (reason === 'io server disconnect') {
        console.warn('[Socket] Server disconnected us - likely authentication issue (user not found in database)');
      }
      setIsConnected(false);
      isConnectingRef.current = false;
    });

    newSocket.on('connect_error', (error) => {
      console.error('[Socket] Connection error:', error.message);
      console.error('[Socket] Connection error details:', error);
      setIsConnected(false);
      isConnectingRef.current = false;
    });

    socketRef.current = newSocket;
    setSocket(newSocket);

    // Connect manually after setup
    console.log('[Socket] Connecting', token ? 'with token' : 'without token');
    newSocket.connect();

    // Cleanup on unmount or token change
    return () => {
      // Don't disconnect if this is just a StrictMode re-run
      // and the socket is still being used
      if (socketRef.current === newSocket) {
        console.log('[Socket] Cleaning up connection');
        newSocket.disconnect();
        socketRef.current = null;
        setSocket(null);
        setIsConnected(false);
        isConnectingRef.current = false;
      }
    };
  }, [token]);

  const contextValue: SocketContextValue = {
    socket,
    isConnected,
    addListener,
    removeListener,
    emit,
  };

  return (
    <SocketContext.Provider value={contextValue}>
      {children}
    </SocketContext.Provider>
  );
};
